import { Container, Graphics, Sprite, type FederatedPointerEvent, type Texture } from 'pixi.js';
import { Input } from '@pixi/ui';
import { COLORS, FONT_FAMILY, RADIUS, BORDER } from '../app/theme';
import { AccessibleButton } from './accessibility';

export interface TextFieldOpts {
  width: number;
  height?: number;
  placeholder?: string;
  value?: string;
  secure?: boolean;
  align?: 'left' | 'center' | 'right';
  maxLength?: number;
  /** Optional gold affordance icon pinned to the RIGHT (RTL leading) edge; text is padded to clear it. */
  icon?: Texture | null;
  /** Optional compact clear control pinned inside the LEFT (RTL trailing) edge. */
  clearable?: boolean;
  clearAccessibilityLabel?: string;
  /** Accessible name applied to @pixi/ui's temporary DOM input while it is editing. */
  accessibilityLabel?: string;
  autocomplete?: string;
  inputMode?: 'none' | 'text' | 'decimal' | 'numeric' | 'tel' | 'search' | 'email' | 'url';
  enterKeyHint?: 'enter' | 'done' | 'go' | 'next' | 'previous' | 'search' | 'send';
}

/**
 * Themed text field over @pixi/ui's Input (which manages a hidden DOM <input> under the hood, so
 * mobile keyboards and Persian IME work). RTL fields default to right alignment.
 */
export class TextField extends Container {
  readonly input: Input;
  private readonly bg: Graphics;
  private readonly w: number;
  private readonly h: number;
  private readonly accessibilityLabel: string;
  private readonly autocomplete: string;
  private readonly inputMode: TextFieldOpts['inputMode'];
  private enterKeyHint: NonNullable<TextFieldOpts['enterKeyHint']>;
  private a11yRaf = 0;
  private gone = false;
  private readOnly = false;
  private keyboardEnter?: () => void;
  private configuredInput?: HTMLInputElement;
  private clearControl?: Container;
  private clearAccessibility?: AccessibleButton;

  constructor(opts: TextFieldOpts) {
    super();
    this.w = opts.width;
    this.h = opts.height ?? 56;
    this.accessibilityLabel = opts.accessibilityLabel ?? opts.placeholder ?? 'ورودی متن';
    this.autocomplete = opts.autocomplete ?? 'off';
    this.inputMode = opts.inputMode;
    this.enterKeyHint = opts.enterKeyHint ?? 'done';
    // Dark wood-bar look (real game): low corner radius, a soft dim border instead of bright gold.
    this.bg = new Graphics()
      .roundRect(0, 0, this.w, this.h, RADIUS.field)
      .fill({ color: COLORS.inputBg })
      .stroke({ width: BORDER.thin, color: COLORS.fieldBorder });

    // An optional leading icon sits at the right edge (RTL); reserve that strip in the text padding so
    // the value/placeholder never slides under it. A faint divider separates the icon from the text.
    const iconTex = opts.icon ?? null;
    const iconSize = iconTex ? Math.round(this.h * 0.46) : 0;
    const iconZone = iconTex ? iconSize + 22 : 0; // icon + gaps + divider
    const rightPad = 16 + iconZone;
    const clearSize = opts.clearable ? Math.min(44, this.h - 16) : 0;
    const leftPad = 16 + (clearSize ? clearSize + 12 : 0);

    this.input = new Input({
      bg: this.bg,
      placeholder: opts.placeholder ?? '',
      value: opts.value ?? '',
      secure: opts.secure ?? false,
      maxLength: opts.maxLength ?? 0,
      align: opts.align ?? 'right',
      padding: [14, rightPad, 14, leftPad],
      textStyle: { fontFamily: FONT_FAMILY, fontSize: 28, fill: COLORS.text },
    });
    this.addChild(this.input);

    if (iconTex) {
      const cx = this.w - 16 - iconSize / 2;
      const divX = this.w - 16 - iconSize - 11;
      this.addChild(
        new Graphics().moveTo(divX, this.h * 0.24).lineTo(divX, this.h * 0.76).stroke({ width: 1, color: COLORS.fieldBorder, alpha: 0.7 }),
      );
      const s = new Sprite(iconTex);
      s.anchor.set(0.5);
      s.scale.set(iconSize / Math.max(iconTex.width, iconTex.height));
      s.position.set(cx, this.h / 2);
      s.alpha = 0.92;
      this.addChild(s);
    }

    if (clearSize) {
      const clear = new Container();
      clear.position.set(10, (this.h - clearSize) / 2);
      clear.eventMode = 'static';
      clear.cursor = 'pointer';
      clear.addChild(
        new Graphics()
          .roundRect(0, 0, clearSize, clearSize, RADIUS.control)
          .fill({ color: COLORS.panelSoft, alpha: 0.9 })
          .stroke({ width: 1, color: COLORS.fieldBorder, alpha: 0.9 }),
        new Graphics()
          .moveTo(clearSize * 0.34, clearSize * 0.34)
          .lineTo(clearSize * 0.66, clearSize * 0.66)
          .moveTo(clearSize * 0.66, clearSize * 0.34)
          .lineTo(clearSize * 0.34, clearSize * 0.66)
          .stroke({ width: 2.5, color: COLORS.textDim, cap: 'round' }),
      );
      const stop = (event: FederatedPointerEvent): void => event.stopPropagation();
      clear.on('pointerdown', stop);
      clear.on('pointertap', (event: FederatedPointerEvent) => {
        event.stopPropagation();
        this.clear();
      });
      this.clearControl = clear;
      this.addChild(clear);
      this.clearAccessibility = new AccessibleButton(
        clear,
        clearSize,
        clearSize,
        opts.clearAccessibilityLabel ?? `پاک کردن ${this.accessibilityLabel}`,
        () => this.clear(),
      );
      this.input.onChange.connect(() => this.syncClearControl());
      this.syncClearControl();
    }

    const syncAccessibility = (): void => {
      if (this.gone) return;
      const domInput = (this.input as unknown as { input?: HTMLInputElement }).input;
      if (domInput) {
        if (this.configuredInput !== domInput) {
          this.configuredInput = domInput;
          domInput.classList.add('pixi-text-field-editor');
          // @pixi/ui sizes this hidden editor in unscaled design pixels, which can otherwise leave an
          // invisible hit target over the next canvas control. It only needs programmatic focus.
          domInput.style.pointerEvents = 'none';
          domInput.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' || event.isComposing) return;
            event.preventDefault();
            const callback = this.keyboardEnter;
            if (callback) queueMicrotask(callback);
          }, true);
        }
        domInput.setAttribute('aria-label', this.accessibilityLabel);
        domInput.setAttribute('autocomplete', this.autocomplete);
        domInput.setAttribute('aria-readonly', String(this.readOnly));
        domInput.readOnly = this.readOnly;
        if (this.inputMode) domInput.inputMode = this.inputMode;
        domInput.enterKeyHint = this.enterKeyHint;
        domInput.dir = 'rtl';
      }
      this.a11yRaf = requestAnimationFrame(syncAccessibility);
    };
    this.a11yRaf = requestAnimationFrame(syncAccessibility);
  }

  get value(): string {
    return this.input.value;
  }
  set value(v: string) {
    this.input.value = v;
    this.syncClearControl();
  }

  /** Clear the current value and notify ordinary onChange subscribers. */
  clear(): void {
    if (this.readOnly || !this.input.value) return;
    this.input.value = '';
    this.syncClearControl();
    this.input.onChange.emit('');
  }

  onChange(fn: (text: string) => void): void {
    this.input.onChange.connect(fn);
  }
  onEnter(fn: (text: string) => void): void {
    this.input.onEnter.connect(fn);
  }

  /** Fires only for the keyboard Enter/Next/Done key, never for an ordinary outside blur. */
  onKeyboardEnter(fn: () => void): void {
    this.keyboardEnter = fn;
  }

  setEnterKeyHint(hint: NonNullable<TextFieldOpts['enterKeyHint']>): void {
    this.enterKeyHint = hint;
    if (this.configuredInput) this.configuredInput.enterKeyHint = hint;
  }

  /** Begin editing programmatically, used by fast Next-field form flows. */
  focus(): void {
    if (this.readOnly) return;
    const raw = this.input as unknown as { activation?: boolean; handleActivation?: () => void };
    raw.activation = true;
    raw.handleActivation?.();
  }

  /** Commit the current edit and remove the temporary DOM input. */
  blur(): void {
    const raw = this.input as unknown as { input?: HTMLInputElement; stopEditing?: () => void };
    if (raw.input) raw.input.blur();
    else raw.stopEditing?.();
  }

  setReadonly(readonly: boolean): void {
    this.readOnly = readonly;
    this.input.eventMode = readonly ? 'none' : 'static';
    this.input.cursor = readonly ? 'default' : 'text';
    this.alpha = readonly ? 0.6 : 1;
    this.syncClearControl();

    // If the binding response arrives while the field is being edited, immediately stop the hidden
    // DOM editor so keyboard events cannot mutate the now-authoritative server username.
    const domInput = (this.input as unknown as { input?: HTMLInputElement }).input;
    if (domInput) {
      domInput.readOnly = readonly;
      domInput.setAttribute('aria-readonly', String(readonly));
      if (readonly) domInput.blur();
    }
  }

  private syncClearControl(): void {
    if (!this.clearControl) return;
    const available = !this.readOnly && this.input.value.length > 0;
    this.clearControl.visible = available;
    this.clearControl.eventMode = available ? 'static' : 'none';
    this.clearControl.cursor = available ? 'pointer' : 'default';
    this.clearAccessibility?.setDisabled(!available);
  }

  override destroy(options?: Parameters<Container['destroy']>[0]): void {
    this.blur();
    this.gone = true;
    cancelAnimationFrame(this.a11yRaf);
    this.clearAccessibility?.destroy(options);
    super.destroy(options);
  }
}
